import { type Category, type Resource, type ResourceTypeEntity } from '../api/resourceService';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

export default function ResourceCard({
  resource,
  categories,
  resourceTypes,
  onEdit,
  onDelete,
  viewMode = 'compact',
}: {
  resource: Resource;
  categories: Category[];
  resourceTypes: ResourceTypeEntity[];
  onEdit?: () => void;
  onDelete?: () => void;
  viewMode?: 'standard' | 'compact';
}) {
  const { isAdmin } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';

  const category = categories.find(c => c.id === resource.categoryId);
  const resourceType = resourceTypes.find(t => t.id === resource.typeId);

  // Configuración dinámica basada en el modo de vista
  const cardConfig = {
    standard: {
      minHeight: 'min-h-[280px]',
      padding: 'p-6',
      titleSize: 'text-xl',
      descriptionHeight: 'h-[72px]',
      categoryMaxWidth: 'max-w-[120px]',
      tagMaxWidth: 'max-w-[100px]',
      buttonPadding: 'p-2',
      iconSize: 'text-lg'
    },
    compact: {
      minHeight: 'min-h-[180px]',
      padding: 'p-3',
      titleSize: 'text-base',
      descriptionHeight: 'h-[20px]',
      categoryMaxWidth: 'max-w-[70px]',
      tagMaxWidth: 'max-w-[70px]',
      buttonPadding: 'p-1',
      iconSize: 'text-sm'
    }
  };

  const config = cardConfig[viewMode];

  // Refs para detectar truncamiento
  const titleRef = useRef<HTMLHeadingElement>(null);
  const categoryRef = useRef<HTMLSpanElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const resourceTypeRef = useRef<HTMLSpanElement>(null);
  const tagRef = useRef<HTMLSpanElement>(null);

  // Estados para controlar tooltips
  const [showTitleTooltip, setShowTitleTooltip] = useState(false);
  const [showCategoryTooltip, setShowCategoryTooltip] = useState(false);
  const [showDescriptionTooltip, setShowDescriptionTooltip] = useState(false);
  const [showResourceTypeTooltip, setShowResourceTypeTooltip] = useState(false);
  const [isCategoryTooltipOpen, setIsCategoryTooltipOpen] = useState(false);
  const [isTitleTooltipOpen, setIsTitleTooltipOpen] = useState(false);
  const [isDescriptionTooltipOpen, setIsDescriptionTooltipOpen] = useState(false);
  const [isResourceTypeTooltipOpen, setIsResourceTypeTooltipOpen] = useState(false);
  const [isTagsTooltipOpen, setIsTagsTooltipOpen] = useState(false);
  const [showIndividualTagTooltip, setShowIndividualTagTooltip] = useState(false);

  // Detectar truncamiento en montaje y cambios
  useEffect(() => {
    if (titleRef.current) {
      const element = titleRef.current;
      const isTruncated = element.scrollWidth > element.clientWidth;
      setShowTitleTooltip(isTruncated);
    }
  }, [resource.title, lang]);

  useEffect(() => {
    if (resourceTypeRef.current) {
      const element = resourceTypeRef.current;
      const isTruncated = element.scrollWidth > element.clientWidth;
      setShowResourceTypeTooltip(isTruncated);
    }
  }, [resourceType?.name, lang]);

  useEffect(() => {
    if (tagRef.current) {
      const element = tagRef.current;
      const isTruncated = element.scrollWidth > element.clientWidth;
      setShowIndividualTagTooltip(isTruncated);
    }
  }, [resource.tags, lang]);

  // Detectar truncamiento de categoría al montar
  useEffect(() => {
    if (categoryRef.current) {
      const element = categoryRef.current;
      const isTruncated = element.scrollWidth > element.clientWidth;
      setShowCategoryTooltip(isTruncated);
    }
  }, [category?.name, lang]);

  // Detectar truncamiento después del montaje completo
  useEffect(() => {
    const timer = setTimeout(() => {
      if (categoryRef.current) {
        const element = categoryRef.current;
        const isTruncated = element.scrollWidth > element.clientWidth;
        setShowCategoryTooltip(isTruncated);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [category?.name, lang]);

  useEffect(() => {
    if (categoryRef.current) {
      const element = categoryRef.current;
      const isTruncated = element.scrollWidth > element.clientWidth;
      setShowCategoryTooltip(isTruncated);
    }
  }, [category?.name, lang]);

  useEffect(() => { }, [showCategoryTooltip, isCategoryTooltipOpen]);

  useEffect(() => { }, [category, lang, resource.categoryId]);

  useEffect(() => {
    const handleResize = () => {
      if (categoryRef.current) {
        const element = categoryRef.current;
        const isTruncated = element.scrollWidth > element.clientWidth;
        setShowCategoryTooltip(isTruncated);
      }

      if (titleRef.current) {
        const element = titleRef.current;
        const isTruncated = element.scrollWidth > element.clientWidth;
        setShowTitleTooltip(isTruncated);
      }

      if (descriptionRef.current) {
        const element = descriptionRef.current;
        const isTruncated = element.scrollHeight > element.clientHeight;
        setShowDescriptionTooltip(isTruncated);
      }

      if (resourceTypeRef.current) {
        const element = resourceTypeRef.current;
        const isTruncated = element.scrollWidth > element.clientWidth;
        setShowResourceTypeTooltip(isTruncated);
      }

      if (tagRef.current) {
        const element = tagRef.current;
        const isTruncated = element.scrollWidth > element.clientWidth;
        setShowIndividualTagTooltip(isTruncated);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const isTruncated = element.scrollHeight > element.clientHeight;
      setShowDescriptionTooltip(isTruncated);
    }
  }, [resource.description, lang]);

  return (
    <div className={`bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-lg overflow-hidden transform hover:-translate-y-1 transition-all duration-300 hover:shadow-xl ${config.minHeight} flex flex-col`}>
      <div className={`${config.padding} flex-shrink`}>
        {/* Header con categoría y botones de acción */}
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0 max-w-[150px] sm:max-w-[180px] md:max-w-[200px] lg:max-w-none">
            {/* Badge de categoría */}
            <div className="relative group">
              <span
                ref={categoryRef}
                className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-[var(--text-default)] bg-[var(--surface)] border border-[var(--border)] truncate block ${config.categoryMaxWidth} ${showCategoryTooltip ? 'cursor-pointer' : ''}`}
                onClick={() => showCategoryTooltip && setIsCategoryTooltipOpen(true)}
                title={showCategoryTooltip ? "Click para ver nombre completo" : ""}
              >
                {category?.name[lang] || 'Sin categoría'}
              </span>

              {/* Tooltip de categoría en click - hacia abajo, solo si está truncado */}
              {showCategoryTooltip && isCategoryTooltipOpen && (
                <div
                  className="absolute top-full left-0 mt-2 p-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg z-50 min-w-48 max-h-48 overflow-y-auto"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-xs font-medium text-[var(--text-default)]">Categoría:</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsCategoryTooltipOpen(false);
                      }}
                      className="text-[var(--text-muted)] hover:text-[var(--text-default)] transition-colors duration-200"
                      aria-label="Cerrar tooltip"
                    >
                      <span className="material-icons text-sm">close</span>
                    </button>
                  </div>
                  <div className="text-sm text-[var(--text-muted)]">
                    {category?.name[lang] || 'Sin categoría'}
                  </div>
                </div>
              )}
            </div>

            {/* Título del recurso */}
            <div className="mt-2 relative group">
              <h3
                ref={titleRef}
                className={`${config.titleSize} font-bold text-[var(--text-default)] truncate block w-full ${showTitleTooltip ? 'cursor-pointer' : ''}`}
                onClick={() => showTitleTooltip && setIsTitleTooltipOpen(true)}
                title={showTitleTooltip ? "Click para ver título completo" : ""}
              >
                {resource.title[lang]}
              </h3>

              {showTitleTooltip && isTitleTooltipOpen && (
                <div
                  className="absolute top-full left-0 mt-2 p-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg z-50 min-w-48 max-w-64 max-h-48 overflow-y-auto"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-bold text-[var(--text-default)]">Título:</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTitleTooltipOpen(false);
                      }}
                      className="text-[var(--text-muted)] hover:text-[var(--text-default)] transition-colors duration-200"
                      aria-label="Cerrar tooltip"
                    >
                      <span className="material-icons text-sm">close</span>
                    </button>
                  </div>
                  <div className="text-sm text-[var(--text-muted)]">
                    {resource.title[lang]}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2 relative group">
              <p
                ref={descriptionRef}
                className={`text-[var(--text-muted)] text-sm leading-relaxed ${config.descriptionHeight} overflow-hidden ${showDescriptionTooltip ? 'cursor-pointer' : ''}`}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: viewMode === 'standard' ? 3 : 1,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.5rem'
                }}
                onClick={() => showDescriptionTooltip && setIsDescriptionTooltipOpen(true)}
                title={showDescriptionTooltip ? "Click para ver descripción completa" : ""}
              >
                {resource.description[lang]}
              </p>

              {showDescriptionTooltip && isDescriptionTooltipOpen && (
                <div
                  className="fixed inset-0 flex items-center justify-center z-[9999]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDescriptionTooltipOpen(false);
                  }}
                >
                  <div 
                    className="bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-xl p-4 min-w-[280px] max-w-[320px] max-h-48 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="text-sm font-bold text-[var(--text-default)]">Descripción:</div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsDescriptionTooltipOpen(false);
                        }}
                        className="text-[var(--text-muted)] hover:text-[var(--text-default)] transition-colors duration-200"
                        aria-label="Cerrar tooltip"
                      >
                        <span className="material-icons text-sm">close</span>
                      </button>
                    </div>
                    <div className="text-sm text-[var(--text-muted)] leading-relaxed">
                      {resource.description[lang]}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isAdmin && (
            <div className="flex gap-1 flex-shrink-0">
              <button
                className={`${config.buttonPadding} text-[var(--text-muted)] hover:text-[var(--surface)] hover:bg-[var(--text-default)] rounded-lg transition-colors duration-200`}
                onClick={onEdit}
                aria-label="Editar recurso"
              >
                <span className={`material-icons ${config.iconSize}`}>edit</span>
              </button>
              <button
                className={`${config.buttonPadding} text-[var(--text-muted)] hover:text-[var(--surface)] hover:bg-[var(--text-default)] rounded-lg transition-colors duration-200`}
                onClick={onDelete}
                aria-label="Eliminar recurso"
              >
                <span className={`material-icons ${config.iconSize}`}>delete</span>
              </button>
            </div>
          )}
        </div>

        {resource.tags.length > 0 && (
          <div className="mt-2 group relative">
            <div className="flex gap-1 overflow-hidden">
              {resource.tags.slice(0, 3).map((tag, index) => (
                <div key={tag} className="relative group">
                  <span
                    ref={index === 0 ? tagRef : undefined}
                    className={`text-xs font-medium inline-block py-1 px-2 rounded-lg text-[var(--text-default)] bg-[var(--surface)] border border-[var(--border)] flex-shrink-0 ${config.tagMaxWidth} truncate`}
                    title={tag.length > 15 ? tag : ""}
                  >
                    {tag}
                  </span>

                  {index === 0 && showIndividualTagTooltip && (
                    <div className="absolute bottom-full left-0 mb-2 p-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg z-50 min-w-32 max-w-48">
                      <div className="text-xs text-[var(--text-muted)]">{tag}</div>
                    </div>
                  )}
                </div>
              ))}
              {resource.tags.length > 3 && (
                <div className="relative group">
                  <span
                    className="text-xs font-medium inline-block py-1 px-2 rounded-lg text-[var(--text-default)] bg-[var(--surface)] border border-[var(--border)] flex-shrink-0 cursor-pointer max-w-[80px] sm:max-w-[100px] md:max-w-[120px] truncate"
                    onClick={() => setIsTagsTooltipOpen(true)}
                    title="Click para ver todos los tags"
                  >
                    +{resource.tags.length - 3}
                  </span>
                </div>
              )}
            </div>

            {resource.tags.length > 3 && isTagsTooltipOpen && (
              <div className="absolute bottom-full left-0 mb-2 p-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg z-50 min-w-48">
                <div className="flex justify-between items-start mb-2">
                  <div className="text-xs font-medium text-[var(--text-default)]">Todos los tags:</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsTagsTooltipOpen(false);
                    }}
                    className="text-[var(--text-muted)] hover:text-[var(--text-default)] transition-colors duration-200"
                    aria-label="Cerrar tooltip"
                  >
                    <span className="material-icons text-sm">close</span>
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {resource.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-xs font-medium inline-block py-1 px-2 rounded-lg text-[var(--text-default)] bg-[var(--surface)] border border-[var(--border)]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-4 pb-2 pt-2 border-t border-[var(--border)] bg-[var(--surface)]/50 m-0 flex items-center justify-between">
        <div className="group relative flex-shrink-0 max-w-[35%] sm:max-w-[40%] md:max-w-[45%]">
          <span
            ref={resourceTypeRef}
            className={`text-xs font-medium inline-flex items-center justify-center py-1 px-2 rounded-lg text-[var(--text-default)] bg-[var(--surface)] border border-[var(--border)] truncate w-full ${showResourceTypeTooltip ? 'cursor-pointer' : ''}`}
            onClick={() => showResourceTypeTooltip && setIsResourceTypeTooltipOpen(true)}
            title={showResourceTypeTooltip ? "Click para ver nombre completo" : ""}
          >
            {resourceType?.name[lang] || 'Tipo desconocido'}
          </span>

          {showResourceTypeTooltip && isResourceTypeTooltipOpen && (
            <div
              className="absolute bottom-full left-0 mb-2 p-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg z-50 min-w-48 max-h-48 overflow-y-auto"
              onWheel={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="text-xs font-medium text-[var(--text-default)]">Tipo de recurso:</div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsResourceTypeTooltipOpen(false);
                  }}
                  className="text-[var(--text-muted)] hover:text-[var(--text-default)] transition-colors duration-200"
                  aria-label="Cerrar tooltip"
                >
                  <span className="material-icons text-sm">close</span>
                </button>
              </div>
              <div className="text-sm text-[var(--text-muted)]">
                {resourceType?.name[lang] || 'Tipo desconocido'}
              </div>
            </div>
          )}
        </div>
        <a
          className="inline-flex items-center text-[var(--text-default)] hover:text-[var(--text-muted)] font-medium text-sm transition-colors duration-200 group flex-shrink-0"
          href={resource.url}
          target="_blank"
          rel="noreferrer"
        >
          Ir al recurso
          <span className="material-icons ml-1 text-base group-hover:translate-x-1 transition-transform duration-200">
            arrow_forward
          </span>
        </a>
      </div>
    </div>
  );
}
